import { Injectable } from '@angular/core';
import { Observable, tap, Subject } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../../environments/environment";

const END_POINT = `${environment.SERVICES_URL}`;
const API_KEY = `${environment.API_KEY}`;

export interface StatusCatalog {
    id: number;
    name: string;
    code: string;
    color: string;
    subcategories: StatusCatalog[];
}

export interface DayMovement {
    movementId: number;
    statusId: number;
    statusName: string;
    statusCode: string;
    statusColor: string;
    serviceId?: number;
    startTime: string;
    endTime: string;
    duration: number;
    comments?: string;
    isOpen?: boolean;
}

export interface VehicleDailyStatus {
    vehicleId: number;
    plates: string;
    color: string;
    brand: number;
    model: number;
    dailyStatus: { [day: number]: DayMovement[] };
}

export interface MonthlyBoardResponse {
    year: number;
    month: number;
    daysInMonth: number;
    vehicles: VehicleDailyStatus[];
}

export interface MovementDetail {
    id: number;
    vehicle: {
        id: number;
        plates: string;
        color: string;
    };
    status: {
        id: number;
        name: string;
        code: string;
        color: string;
    };
    service?: {
        id: number;
    };
    startDate: string;
    endDate?: string;
    comments?: string;
    isManual: boolean;
    createdBy: {
        id: number;
        email: string;
    };
    createdAt: string;
}

export interface UserPermissions {
    canCreateMovements: boolean;
    canEditHistory: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class FleetTrackingService {
    private loadingSubject = new Subject<boolean>();
    public loading$ = this.loadingSubject.asObservable();

    constructor(private http: HttpClient) {}

    /**
     * Control del loader (puedes adaptarlo a tu sistema)
     */
    private controlLoader(show: boolean): void {
        this.loadingSubject.next(show);
    }

    /**
     * Obtener permisos del usuario actual
     */
    getUserPermissions(): Observable<any> {
        const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
        this.controlLoader(true);
        return this.http.get<any>(
            `${END_POINT}/fleet-tracking/user-permissions`,
            { headers }
        ).pipe(
            tap(() => {
                this.controlLoader(false);
            })
        );
    }

    /**
     * Obtener catálogo de estatus
     */
    getStatusCatalog(): Observable<any> {
        const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
        this.controlLoader(true);
        return this.http.get<any>(
            `${END_POINT}/fleet-tracking/status-catalog`,
            { headers }
        ).pipe(
            tap(() => {
                this.controlLoader(false);
            })
        );
    }

    /**
     * Obtener tablero mensual
     */
    getMonthlyBoard(year: number, month: number): Observable<any> {
        const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
        this.controlLoader(true);
        return this.http.get<any>(
            `${END_POINT}/fleet-tracking/monthly-board?year=${year}&month=${month}`,
            { headers }
        ).pipe(
            tap(() => {
                this.controlLoader(false);
            })
        );
    }

    /**
     * Crear nuevo movimiento
     */
    createMovement(data: {
        vehicleId: number;
        statusId: number;
        serviceId?: number | null;
        startDate: string;
        comments?: string;
    }): Observable<any> {
        const body = JSON.stringify(data);
        const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
        this.controlLoader(true);
        return this.http.post<any>(
            `${END_POINT}/fleet-tracking/create-movement`,
            body,
            { headers }
        ).pipe(
            tap(() => {
                this.controlLoader(false);
            })
        );
    }

    /**
     * Cerrar movimiento
     */
    closeMovement(movementId: number, endDate?: string): Observable<any> {
        const body = JSON.stringify({
            movementId,
            endDate: endDate || new Date().toISOString().slice(0, 19).replace('T', ' ')
        });
        const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
        this.controlLoader(true);
        return this.http.put<any>(
            `${END_POINT}/fleet-tracking/close-movement`,
            body,
            { headers }
        ).pipe(
            tap(() => {
                this.controlLoader(false);
            })
        );
    }

    /**
     * Obtener detalles de un movimiento
     */
    getMovementDetails(movementId: number): Observable<any> {
        const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
        this.controlLoader(true);
        return this.http.get<any>(
            `${END_POINT}/fleet-tracking/movement-details?id=${movementId}`,
            { headers }
        ).pipe(
            tap(() => {
                this.controlLoader(false);
            })
        );
    }

    /**
     * Obtener historial de un vehículo
     */
    getVehicleHistory(vehicleId: number, startDate?: string, endDate?: string): Observable<any> {
        const headers = new HttpHeaders().set('X-Api-Key', `${API_KEY}`);
        let url = `${END_POINT}/fleet-tracking/vehicle-history?vehicleId=${vehicleId}`;

        if (startDate) {
            url += `&startDate=${startDate}`;
        }
        if (endDate) {
            url += `&endDate=${endDate}`;
        }

        this.controlLoader(true);
        return this.http.get<any>(url, { headers }).pipe(
            tap(() => {
                this.controlLoader(false);
            })
        );
    }
}
